import { ChangeEvent, FC, useCallback, useEffect, useMemo, useRef, useState, KeyboardEvent, } from "react";
// 组件
import { CloseOutlined, LoadingOutlined, UnorderedListOutlined, } from "@ant-design/icons";
import Dropdown from "./subpage/dropdown";
// 方法
import classNames from "classnames";
import useClickOutside from "../Hooks/useClickOutside";
// 常量
import { SUCCESS_STATUS_CODE, TABLE_COLUMN_EMPTY } from "../constants/constant";
import { AutoMatchProps, DEFAULT_RULES_FIELD_NAMES, RuleProps } from "./interface";
import { DropdownOffset } from "../SuperSelect/interface";
import { getElementScrollLeftAndTop, getElementTopAndLeft, inputMoveEnd } from "../utils/utils";
import './style.less';

/** 自动匹配下拉 */
const AutoMatch: FC<AutoMatchProps> = props => {
  const {
    rules: outRules, loading: outLoading, value: outValue, placeholder, valueEmprtTip, ruleEmprtTip,
    style, className, defaultParams, fieldNames, disabled, dropdownMatchSelectWidth, popupClassName, dropdownStyle, listHeight,
    fetchApi, onBeforeData, onChange, onFocus, onBlur, getPopupContainer,
  } = props;
  const [loading, setLoading] = useState<boolean>(false); // 加载状态
  const [value, setValue] = useState<string>(); // 值
  const [isFocus, setIsFocus] = useState<boolean>(false); // 下拉框是否获取焦点
  const [rules, setRules] = useState<RuleProps[]>(); // 匹配规则
  const [close, setClose] = useState<boolean>(); // 是否显示清除按钮
  const selectRef = useRef<HTMLDivElement>(null); // 下拉框节点
  const inputRef = useRef<HTMLInputElement>(null); // 下拉输入框框节点
  const dropdownRef = useRef<HTMLDivElement>(null); // dropdown节点
  const dropdownOffset = useRef<DropdownOffset>(); // dropdown定位数据
  const dropdownWidth = useRef<number>(); // dropdown宽度
  const selectedRule = useRef<RuleProps>(); // 当前选中的匹配规则
  const isFirst = useRef<boolean>(false); // 是否已经打开过下拉框
  useClickOutside([selectRef, dropdownRef], isFocus, () => {
    setIsFocus(false);
    onBlur?.();
  });
  useEffect(() => {
    const newRules = outRules || rules;
    if (Array.isArray(outValue) && newRules && !value) {
      const findRule = newRules.find(item => item.value === outValue[0]);
      if (findRule) {
        selectedRule.current = findRule;
        setValue(outValue[1]);
      }
    }
  }, [outValue, outRules, rules]);
  useEffect(() => {
    if (outRules) {
      setRules(outRules);
    }
  }, [outRules]);
  useEffect(() => {
    if (!isFocus && !selectedRule.current && value) {
      if (inputRef.current) {
        inputRef.current.value = '';
        setValue(undefined);
      }
    }
  }, [isFocus]);
  useEffect(() => {
    if (!outRules && fetchApi) {
      setLoading(true);
      fetchApi(defaultParams).then(res => {
        if (res.code === SUCCESS_STATUS_CODE) {
          const data = onBeforeData ? onBeforeData(res.data) : res.data;
          const newRules: RuleProps[] = [];
          if (Array.isArray(data) && data.length) {
            data.forEach(item => {
              const {
                [fieldNames?.label || DEFAULT_RULES_FIELD_NAMES.label]: label,
                [fieldNames?.value || DEFAULT_RULES_FIELD_NAMES.value]: value,
                [fieldNames?.prefix || DEFAULT_RULES_FIELD_NAMES.prefix]: prefix,
              } = item;
              newRules.push({label, value, prefix});
            });
            if (Array.isArray(outValue)) {
              const findRule = newRules.find(item => item.value === outValue[0]);
              if (findRule) {
                selectedRule.current = findRule;
                setValue(outValue[1]);
              }
            }
          }
          setRules(newRules);
        }
      }).finally(() => {
        setLoading(false);
      })
    }
  }, []);
  /** 获取下拉框定位 */
  const handleSetOffset = () => {
    const selectElement = selectRef.current;
    const popupContainerOffset: DropdownOffset = {left: 0, top: 0};
    if (getPopupContainer) {
      const popupContainer: HTMLElement = getPopupContainer();
      const popupOffset = getElementTopAndLeft(popupContainer);
      popupContainerOffset.left = popupOffset.left;
      popupContainerOffset.top = popupOffset.top;
    }
    if (selectElement) {
      const selectHeight = selectElement.clientHeight;
      const selectOffset = getElementTopAndLeft(selectElement);
      const selectScroll = getElementScrollLeftAndTop(selectElement);
      dropdownWidth.current = Math.max(selectElement.clientWidth, dropdownMatchSelectWidth || 0);
      dropdownOffset.current = {
        left: selectOffset.left - popupContainerOffset.left - selectScroll.left,
        top: selectOffset.top + selectHeight - popupContainerOffset.top - selectScroll.top,
      };
    }
  };
  /** 点击下拉框 */
  const handleClickSelect = () => {
    if (disabled || isFocus) return;
    isFirst.current = true;
    inputMoveEnd(inputRef.current);
    handleSetOffset();
    setIsFocus(true);
    onFocus?.();
  };
  /** input值改变 */
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    if (!newValue) selectedRule.current = undefined;
    setValue(newValue);
  };
  /** 点击选项 */
  const handleClickItem = (optionValue: string, optionItem: RuleProps) => {
    selectedRule.current = optionItem;
    inputRef.current?.blur();
    setIsFocus(false);
    value && onChange?.([optionValue, value]);
  };
  /** 鼠标指针移出Input */
  const handleInputMouseLeave = () => {
    if (disabled) return;
    setClose(false);
  };
  /** 鼠标指针移入Input */
  const handleInputMouseEnter = () => {
    if (disabled) return;
    setClose(true);
  };
  /** input框键盘事件 */
  const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
    const keyNum = event.key;
    if (keyNum === 'ArrowDown' || keyNum === 'ArrowUp') {
      event.preventDefault();
    }
  }
  /** 清除 */
  const handleClear = (event: any) => {
    // event.stopPropagation();
    selectedRule.current = undefined;
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setValue(undefined);
    onChange?.(undefined);
  };
  /** 禁用显示 */
  const detailRender = () => {
    if (selectedRule.current && value) {
      return `${selectedRule.current.label}: ${value}`;
    }
    return TABLE_COLUMN_EMPTY;
  };
  const renderIcon = useMemo(() => {
    if (loading) {
      return <LoadingOutlined />
    }
    if (close && value) {
      return (
        <CloseOutlined
          className="yb-associate-select-input-close"
          onClick={handleClear}
        />
      );
    }
    return <UnorderedListOutlined />;
  }, [loading, close, value]);
  const wrapperClasses = useMemo(() => {
    return classNames('yb-associate-select', className, {
      'yb-associate-select-focus': isFocus,
      'yb-associate-select-disabled': disabled,
    });
  }, [isFocus, disabled]);
  return (
    <>
      {
        disabled ? (
          <div className="yb-line-ellipsis-1" style={{ borderBottom: 'dashed 1px #adb5bd' }}>
            {detailRender()}
          </div>
        ) : (
          <div
            ref={selectRef}
            style={style}
            className={wrapperClasses}
            onClick={handleClickSelect}
            onMouseLeave={handleInputMouseLeave}
            onMouseEnter={handleInputMouseEnter}
          >
            <div className={classNames('yb-associate-select-prefix', {'not-selected': !selectedRule.current})}>
              {selectedRule.current ? `${selectedRule.current.label}:` : ''}
            </div>
            <input
              ref={inputRef}
              placeholder={placeholder}
              className='yb-associate-select-input'
              type='text'
              value={value}
              readOnly={disabled}
              onChange={handleChange}
              onKeyDown={handleInputKeydown}
            />
            <div className="yb-associate-select-suffix">{renderIcon}</div>
          </div>
        )
      }
      {
        isFirst.current && (
          <Dropdown
            rules={rules}
            popupClassName={popupClassName}
            dropdownStyle={dropdownStyle}
            dropdownRef={dropdownRef}
            show={isFocus}
            left={dropdownOffset.current?.left}
            top={dropdownOffset.current?.top}
            width={dropdownWidth.current || 220}
            listHeight={listHeight}
            loading={outLoading || loading}
            onClickItem={handleClickItem}
            value={value}
            selectedRule={selectedRule.current}
            valueEmprtTip={valueEmprtTip}
            ruleEmprtTip={ruleEmprtTip}
            getPopupContainer={getPopupContainer}
          />
        )
      }
    </>
  );
};

AutoMatch.defaultProps = {
  fieldNames: DEFAULT_RULES_FIELD_NAMES,
  listHeight: 320,
  placeholder: '请输入',
  valueEmprtTip: '请输入',
  ruleEmprtTip: '暂无匹配选项',
};

export default AutoMatch;
